import React, { useCallback, useEffect, useState } from 'react';
import {
    Typography,
    IconButton,
    useTheme,
    IOption,
    CircularProgress,
    TextField,
    Icon,
    useAppSelector,
    useAppDispatch,
    Tooltip,
} from '../../../base';
import Autocomplete, {
    AutocompleteRenderInputParams,
} from '@mui/material/Autocomplete';
import { IPercentageColorThresholds } from './RightSidemenu.interfaces';
import {
    MenuWrapper,
    HeaderWrapper,
    CompanySelectWrapper,
    CategoriesListWrapper,
    CategoryItemStyled,
    CategoryContentWrapper,
    PercentageCircleStyled,
    PercentageText,
    CategoryName,
    FooterLinkWrapper,
    FooterButton,
} from './RightSidemenu.styles';
import CreateCompanyModal from '../../ToolsComponents/CreateCompanyModal';
import { toolsActions, toolsSelectors, userSelectors } from '../../../actions';
import { IClientQuestionCategory } from '../../../common';
import { RoutePaths } from '../../../routes/RoutePaths';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const defaultColorThresholds: IPercentageColorThresholds = {
    red: 30, // up to 30 is red
    orange: 50, // up to 50 is orange
    yellow: 75, // up to 75 is yellow
    // Green is above 75
};

const RightSidemenuComponent = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const selectedCompany = useAppSelector(toolsSelectors.getActiveCompany);
    const [isCreateCompanyModalOpen, setIsCreateCompanyModalOpen] =
        useState(false);

    const userData = useAppSelector(userSelectors.getMyUser);
    const companyCategories = useAppSelector(
        toolsSelectors.getCompanyCategories
    );

    const handleCompanyChange = useCallback(
        (_event: React.SyntheticEvent, newValue: IOption | undefined) => {
            dispatch(toolsActions.setActiveCompany(newValue || null));
            dispatch(toolsActions.setActiveChatId(null));
            dispatch(
                toolsActions.setActiveChatHistory({
                    chatHistory: [],
                    generatedContent: [],
                })
            );
            if (newValue) {
                dispatch(
                    toolsActions.fetchCompanyCategories({
                        companyId: newValue.value || '',
                    })
                );
                dispatch(
                    toolsActions.fetchChatsList({
                        companyId: newValue.value || '',
                    })
                );
            }
        },
        [dispatch]
    );

    useEffect(() => {
        if (userData) {
            if (
                userData?.toolsCompanyProfiles?.length > 0 &&
                !selectedCompany
            ) {
                const firstCompany = userData.toolsCompanyProfiles[0];
                dispatch(toolsActions.setActiveCompany(firstCompany));
                handleCompanyChange({} as React.SyntheticEvent, firstCompany);
            }
            if (userData?.toolsCompanyProfiles?.length === 0) {
                setIsCreateCompanyModalOpen(true);
            }
        }
    }, [handleCompanyChange, userData, dispatch, selectedCompany]);

    const handleCategoryClick = (category: IClientQuestionCategory) => {
        dispatch(toolsActions.setActiveCompanyQuestionsCategory(category));
        dispatch(toolsActions.setActiveEditQuestionAnswer(null));
        const navPath = RoutePaths.companyDetails;
        navigate(navPath);
        console.log('Category clicked:', category);
    };

    const handleViewHistoryClick = () => {
        navigate(RoutePaths.viewAnswerHistory);
    };

    const getPercentageColor = (
        percentage: number,
        thresholds: IPercentageColorThresholds
    ): string => {
        if (percentage <= thresholds.red) return theme.palette.error.main;
        if (percentage <= thresholds.orange) return theme.palette.warning.main;
        if (percentage <= thresholds.yellow) return theme.palette.info.main; // Or a custom yellow
        return theme.palette.success.main;
    };

    return (
        <MenuWrapper>
            <HeaderWrapper>
                <Typography variant="h4" color="primary">
                    {t('tools.rightSideMenu.myCompany')}
                </Typography>
                <Tooltip title={t('tools.rightSideMenu.createNewCompany')}>
                    <div>
                        <IconButton
                            onClick={() => setIsCreateCompanyModalOpen(true)}
                            color="secondary"
                            aria-label="create new company"
                            sx={{ height: 24, width: 24 }}
                        >
                            <Icon
                                name={'AddCircleOutlineOutlinedIcon'}
                                sx={{
                                    color: theme.palette.secondary.main,
                                    height: '24px',
                                    width: '24px',
                                }}
                            />
                        </IconButton>
                    </div>
                </Tooltip>
            </HeaderWrapper>

            <CompanySelectWrapper>
                <Autocomplete
                    options={userData?.toolsCompanyProfiles || []}
                    getOptionLabel={(option: IOption) => option.label}
                    value={selectedCompany || null}
                    onChange={handleCompanyChange}
                    isOptionEqualToValue={(option: IOption, value: IOption) =>
                        option.value === value.value
                    }
                    size="small"
                    renderInput={(params: AutocompleteRenderInputParams) => (
                        <TextField
                            {...params}
                            label={t('tools.rightSideMenu.selectCompany')}
                            variant="outlined"
                        />
                    )}
                    fullWidth
                    disableClearable
                />
            </CompanySelectWrapper>

            <CategoriesListWrapper>
                {companyCategories?.map((category: IClientQuestionCategory) => {
                    const circleColor = getPercentageColor(
                        category.percentage,
                        defaultColorThresholds
                    );
                    return (
                        <CategoryItemStyled
                            key={category.id}
                            onClick={() => handleCategoryClick(category)}
                            elevation={1}
                        >
                            <CategoryContentWrapper>
                                <CategoryName variant="subtitle1">
                                    {category.name}
                                </CategoryName>
                                <PercentageCircleStyled>
                                    <CircularProgress
                                        variant="determinate"
                                        value={100}
                                        sx={{
                                            color: theme.palette.grey[300],
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            zIndex: 1,
                                        }}
                                        size={48}
                                        thickness={4}
                                    />
                                    <CircularProgress
                                        variant="determinate"
                                        value={category.percentage}
                                        sx={{
                                            color: circleColor,
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            zIndex: 2,
                                        }}
                                        size={48}
                                        thickness={4}
                                    />
                                    <PercentageText>
                                        {`${category.percentage}%`}
                                    </PercentageText>
                                </PercentageCircleStyled>
                            </CategoryContentWrapper>
                        </CategoryItemStyled>
                    );
                })}
            </CategoriesListWrapper>

            <FooterLinkWrapper>
                <FooterButton
                    onClick={handleViewHistoryClick}
                    variant="text"
                    color="primary"
                >
                    {t('tools.rightSideMenu.viewAllAnswerHistory')}
                </FooterButton>
            </FooterLinkWrapper>
            <CreateCompanyModal
                isModalOpen={isCreateCompanyModalOpen}
                setIsModalOpen={setIsCreateCompanyModalOpen}
                noCloseMode={userData?.toolsCompanyProfiles?.length === 0}
            />
        </MenuWrapper>
    );
};

export default RightSidemenuComponent;
